<script setup>
import LegalPage from "./LegalPage.vue";
</script>

<script>
export default { inheritAttrs: false };
</script>

<template>
  <LegalPage
    title="TalentSage Global Privacy Policy"
    version=""
    effective="October 2026"
  >
    <!-- 1 -->
    <section id="purpose">
      <h2>1. Purpose, Scope &amp; Executive Leadership Architecture</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          This Privacy Policy explains how TalentSage protects your personal
          data across our cloud platforms, diagnostic tools and executive
          coaching services. All privacy inquiries are managed by our Head of
          Privacy at Privacy@TalentSage.com.
        </p>
      </div>
      <p>
        <strong>1.1 Global Privacy Commitment.</strong> TalentSage (comprising
        TalentSage LLC and TalentSage UK Limited) is dedicated to protecting
        the privacy, security and data dignity of all individuals who interact
        with our Cloud Software-with-a-Service (SwaS) platforms, diagnostic
        engines, 360º feedback tools, executive coaching programs and public
        websites.
      </p>
      <p>
        <strong>1.2 Executive Privacy Contact.</strong> All privacy inquiries,
        Subject Access Requests (SARs), data correction requests and regulatory
        communications are overseen directly by the TalentSage Head of Privacy.
        You can reach the Privacy Office directly via email at
        Privacy@TalentSage.com.
      </p>
      <p>
        <strong>1.3 Regulatory Architecture.</strong> This Policy aligns with
        global privacy laws, including the EU General Data Protection
        Regulation (EU GDPR), UK GDPR, Swiss Federal Act on Data Protection
        (FADP) and the EU-U.S., UK Extension, and Swiss-U.S. Data Privacy
        Frameworks (DPF).
      </p>
    </section>

    <!-- 2 -->
    <section id="entities">
      <h2>2. Dual Legal Entities &amp; Contracting Jurisdiction Mapping</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          TalentSage operates through two legal entities. US users are handled
          by TalentSage LLC under US laws, while UK, EU and international users
          are governed by TalentSage UK Limited under English data protection
          laws.
        </p>
      </div>
      <p>
        <strong>2.1 Contracting Entity.</strong> Personal data processing
        responsibilities are mapped between our operating entities based on
        your geographic location and purchasing relationship:
      </p>
      <ul>
        <li>
          <strong>United States &amp; Americas.</strong> TalentSage LLC
          (Delaware reg #4343380, headquartered in Allen, TX, USA) acts as the
          contracting entity and primary data controller/processor for all
          users, clients and platform transactions originating within the
          United States of America under US federal and state privacy
          frameworks.
        </li>
        <li>
          <strong>United Kingdom, EU/EEA &amp; International.</strong>
          TalentSage UK Limited (Company #14668801, registered in England &amp;
          Wales, UK) acts as the contracting entity and primary data
          controller/processor for all users, clients and platform transactions
          originating within the United Kingdom, European Union, European
          Economic Area (EEA), Switzerland and international jurisdictions
          outside the US under UK GDPR, EU GDPR, and the Data Protection Act
          2018.
        </li>
      </ul>
    </section>

    <!-- 3 -->
    <section id="roles">
      <h2>3. Data Roles: Enterprise Controllers vs. Direct Individual Controllers</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          When participating through your employer, your company (enterprise)
          acts as the Data Controller and sets the rules for your program. For
          independent personal purchases or Data Passport accounts, TalentSage
          acts as your Independent Data Controller.
        </p>
      </div>
      <p>
        <strong>3.1 Operational Roles.</strong> TalentSage processes personal
        data under two distinct operational roles depending on your engagement
        model:
      </p>
      <ul>
        <li>
          <strong>A. Enterprise &amp; Sponsoring Employer Programs (Data
          Processor).</strong> For corporate talent initiatives, university
          programs and sponsored executive coaching, the sponsoring employer or
          organization acts as the Data Controller. TalentSage acts strictly as
          a Data Processor operating under the documented instructions of the
          sponsoring organization and the corporate contract stack (Master
          Services Agreement v5.0, Data Processing Addendum Exhibit B and Sales
          Orders).
        </li>
        <li>
          <strong>B. Direct B2C Buyers &amp; Data Passport Holders (Data
          Controller).</strong> For direct individual platform seat buyers,
          independent coaches purchasing self-assessments, sole proprietors and
          participants who have converted their profile into a personal
          TalentSage Data Passport Account, TalentSage acts as an Independent
          Data Controller under this Privacy Policy and our Terms of Service.
        </li>
      </ul>
      <p>
        <strong>3.2 Client Guarantee of Legitimate Basis.</strong> The
        sponsoring employer or client entity warrants and guarantees that it
        holds a valid legal basis (such as GDPR Article 6 legitimate interest or
        performance of contract) to share employee or participant data with
        TalentSage for professional development.
      </p>
      <p>
        <strong>3.3 Enterprise Controller Variance &amp; Liability
        Allocation.</strong> For enterprise programs, any deviations from this
        standard Privacy Policy or platform baseline processing parameters
        (such as customized data retention periods, modified reporting
        thresholds or revoked data portability notices) are governed strictly by
        agreements made at the request of the sponsoring Data Controller's
        instructions and corporate contract stack. The sponsoring Data
        Controller assumes all responsibility for communicating any differences
        or deviations in these parameters to data subjects, unless they have
        explicitly permitted and instructed TalentSage to communicate them for
        operational efficiency and logistical purposes. TalentSage assumes no
        responsibility for any decisions, instructions, differences, or
        deviations required or directed by the client/Data Controller.
      </p>
    </section>

    <!-- 4 -->
    <section id="data-categories">
      <h2>4. Categories of Data Collected &amp; Purpose of Processing</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          We collect basic profile info, 360º survey responses, diagnostic
          scores and coaching goals strictly to deliver diagnostic reports and
          executive coaching. We NEVER sell your data or use it for marketing.
        </p>
      </div>
      <p>
        <strong>4.1 Data Categories.</strong> We collect and process the
        following categories of personal data strictly for legitimate
        professional development and service delivery purposes:
      </p>
      <ul>
        <li>
          <strong>Identity &amp; Profile Data.</strong> Name, email address, job
          title, organizational department, geographic location and platform
          login credentials.
        </li>
        <li>
          <strong>Diagnostic &amp; Survey Data.</strong> Self-assessment
          inputs, 360º rater feedback from managers, peers, direct reports,
          internal stakeholders and external stakeholders, qualitative open-text
          survey comments, psychometric diagnostic scores and interpersonal
          dynamics scores.
        </li>
        <li>
          <strong>Coaching &amp; Facilitation Records.</strong> Developmental
          goals, action planning notes, session prep logs and coach-coachee
          shared objectives established during 1-to-1 or group coaching
          sessions.
        </li>
        <li>
          <strong>Technical &amp; Usage Data.</strong> IP addresses, browser
          type, platform access timestamps, feature utilization logs and device
          metadata collected automatically for system security, audit
          compliance and operational troubleshooting.
        </li>
        <li>
          <strong>Commercial &amp; Transaction Data.</strong> For clients,
          individual buyers, coaches, and contributors, we process transaction
          records, seat subscription terms, invoice histories, and payout
          records (including bank account numbers and sort codes/IBANs
          necessary for billing, coach payouts, accounting, and legal
          compliance). Online card processing and electronic fund transfers are
          managed securely by tier-1 PCI-DSS compliant and regulated payment
          partners (e.g., Stripe, Wise, Revolut); TalentSage stores no payment
          card details on its servers.
        </li>
      </ul>
    </section>

    <!-- 5 -->
    <section id="ai-governance">
      <h2>5. Closed AI Governance, Sub-Processor Ecosystem &amp; Security</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          Your data is completely private. We NEVER use your personal data,
          survey answers or coaching notes to train public AI models.
          Third-party AI meeting bots (like Otter or Fathom) are strictly
          prohibited during coaching sessions.
        </p>
      </div>
      <p>
        <strong>5.1 Zero Public Model Training Guarantee.</strong> TalentSage
        enforces strict, closed enterprise AI governance. Your survey
        responses, qualitative feedback comments, 360º rater breakdowns and
        coaching notes are NEVER used, sold, licensed or retained to train
        public machine learning or Large Language Models (LLMs) (including
        models operated by OpenAI, Google, Anthropic, or Meta).
      </p>
      <p>
        <strong>5.2 Closed Enterprise Infrastructure.</strong> All platform AI
        features (such as automated report synthesis and developmental
        reflection prompts) operate within closed, enterprise-grade cloud
        environments configured with Zero Data Retention (ZDR) architecture.
        Personal data is processed transiently in memory and is immediately
        wiped after generating your report.
      </p>
      <p>
        <strong>5.3 Sub-Processor Transparency.</strong> TalentSage utilizes
        vetted enterprise sub-processors under formal data protection addenda.
        Primary hosting is maintained on Amazon Web Services (AWS) in secure
        UK/EU data centers and Google Cloud Platform (GCP). All sub-processors
        adhere to ISO 27001, SOC 2 Type II, and GDPR compliance standards.
      </p>
      <p>
        <strong>5.4 Coach Sub-Processors &amp; Session Recording Ban.</strong>
        Coaches and facilitators operate legally as sub-processors bound by
        this Policy. To protect participant psychological safety and privacy,
        third-party AI meeting bots (e.g., Otter, Fathom, Fireflies) and video
        recordings are strictly prohibited during 1-to-1 and group coaching
        sessions.
      </p>
    </section>

    <!-- 6 -->
    <section id="consent-gate">
      <h2>6. Coach Sub-Processor Access &amp; Explicit Participant Consent Gate</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          Your coach cannot automatically see your 360º scores or diagnostic
          reports. A coach can only view your data if you explicitly grant
          permission on the platform.
        </p>
      </div>
      <p>
        <strong>6.1 Consent Gate Requirement.</strong> Assigned coaches or
        executive consultants have NO automatic right to access or view a
        participant's diagnostic scores, 360º rater breakdowns or qualitative
        survey responses. Participants may modify or revoke coach data access
        at any time in their account settings.
      </p>
      <p>
        <strong>6.2 Participant Controlled Access.</strong> Platform data
        visibility is unlocked ONLY when the data subject explicitly grants
        access permission through the platform interface under this Privacy
        Policy and our Terms of Service. Attempting to bypass or manipulate
        platform access permissions or introducing unauthorized session
        recording tools constitutes a direct violation of our Terms of Service.
      </p>
    </section>

    <!-- 7 -->
    <section id="retention">
      <h2>7. Data Retention Horizon &amp; 18-Month Survey Launch Anchor</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          We keep identifiable survey data for 18 months, measured from your
          Survey Launch Date, unless a custom period is instructed by your data
          controller. After 18 months, your survey data is automatically
          anonymized into aggregate research benchmarks, unless you choose to
          convert your profile into a Personal Data Passport Account as
          mentioned in Section 8.
        </p>
      </div>
      <p>
        <strong>7.1 Baseline Retention Horizon.</strong> TalentSage enforces a
        standardized baseline retention horizon of strictly 18 months for all
        identifiable diagnostic and survey data.
      </p>
      <p>
        <strong>7.2 Survey Launch Anchor.</strong> The 18-month retention
        period is measured from the Survey Launch Date (the date the diagnostic
        or 360º feedback survey is launched on the platform), regardless of
        when individual raters submit their feedback or when debrief sessions
        occur.
      </p>
      <p>
        <strong>7.3 Automatic Anonymization &amp; Benchmarking.</strong> Upon
        reaching 18 months from the Survey Launch Date, all personal
        identifiers (names, emails, demographic metadata) are permanently
        scrubbed and deleted. The remaining de-identified quantitative scores
        are automatically converted into anonymized normative research data
        used strictly for global industry benchmarking and statistical
        validation.
      </p>
      <p>
        <strong>7.4 Employer Retention Deviations.</strong> If an enterprise
        employer or client negotiates a custom retention period (e.g., 6, 24 or
        36 months recorded in Schedule F or a Sales Order), the employer/Data
        Controller is strictly responsible for notifying data subjects of this
        deviation, unless TalentSage has been formally instructed in writing to
        issue customized platform notices.
      </p>
    </section>

    <!-- 8 -->
    <section id="portability">
      <h2>8. Data Portability (GDPR Art. 20) &amp; 'Data Passport' Transition</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          When your program finishes or nears its survey retention mark, you
          don't lose your insights. You'll receive a 45-day notice giving you
          two clear options: 1) convert to a free Personal Data Passport Account
          for read-only access to your past reports, or 2) request your raw,
          machine-readable data. Personal Data Passport Accounts can be
          upgraded to launch new assessments, obtain new benchmarks or access
          career development resources whenever you want.
        </p>
      </div>
      <p>
        <strong>8.1 45-Day Statutory Portability Notice.</strong> In accordance
        with GDPR Article 20 and global data protection standards, TalentSage
        provides participants with a statutory 45-day data portability notice
        prior to corporate data anonymization or account closure. TalentSage
        provides this notification by default on behalf of the sponsoring
        enterprise, unless the enterprise Data Controller formally revokes this
        authorization in writing (under Section 8.4) and assumes full
        responsibility for notifying its data subjects.
      </p>
      <p>
        <strong>8.2 Data Passport Conversion.</strong> During this 45-day
        window, participants may convert their enterprise profile into an
        independent TalentSage Personal Data Passport Account. Upon conversion,
        TalentSage becomes an Independent Data Controller under Model 4 of our
        Terms of Service and operates in two distinct phases:
      </p>
      <p class="sub">
        <strong>8.2.a. Free Read-Only Preservation.</strong> Conversion grants
        the user immediate, extended read-only access to view, interact with
        and download all historical diagnostic reports, dashboards and career
        benchmarks generated during their enterprise program at no charge.
      </p>
      <p class="sub">
        <strong>8.2.b. Optional Paid Upgrade for Active Services.</strong> If
        the user subsequently chooses to unlock active platform features, such
        as launching new assessments, generating updated benchmarks, ordering
        survey tokens or engaging new coaching services, the user may upgrade
        their Personal Account by purchasing an active annual individual seat.
      </p>
      <p>
        <strong>8.3 Direct Raw Data Exports.</strong> Participants may view and
        download their generated PDF diagnostic reports and interactive
        dashboards at any time during the life of an active account. Users may
        also request a raw, machine-readable export (JSON/CSV) of their personal
        diagnostic history by emailing Privacy@TalentSage.com.
      </p>
      <p>
        <strong>8.4 Enterprise Portability Revocation.</strong> If a sponsoring
        enterprise explicitly revokes the 45-day portability notice in writing
        (under Sales Order Section 5.3), the employer assumes 100% Data
        Controller responsibility for notifying employees of data destruction
        and indemnifies TalentSage against employee portability claims.
      </p>
    </section>

    <!-- 9 -->
    <section id="rights">
      <h2>9. Data Subject Rights &amp; Individual Empowerment</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          You have full rights over your data. You can access, correct, export
          or request deletion of your personal records at any time by emailing
          Privacy@TalentSage.com.
        </p>
      </div>
      <p>
        <strong>9.1 Enumerated Rights.</strong> Regardless of location,
        TalentSage honors full statutory data subject rights under EU GDPR, UK
        GDPR, Swiss FADP, and US privacy laws, including:
      </p>
      <ul>
        <li>
          <strong>Right of Access (SAR).</strong> The right to request a copy of
          all personal data held about you.
        </li>
        <li>
          <strong>Right to Rectification.</strong> The right to update or
          correct inaccurate or incomplete profile records.
        </li>
        <li>
          <strong>Right to Erasure.</strong> The right to request deletion of
          your personal data ('Right to be Forgotten'), subject to mandatory
          legal or contractual retention rules.
        </li>
        <li>
          <strong>Right to Data Portability.</strong> The right to obtain a
          structured, machine-readable copy of your personal data history (via
          Privacy@TalentSage.com).
        </li>
        <li>
          <strong>Right to Object &amp; Restrict.</strong> The right to object
          to processing based on legitimate interests or request restrictions on
          specific processing activities.
        </li>
        <li>
          <strong>Right to Withdraw Consent.</strong> The right to withdraw
          consent at any time where processing is based on consent (such as
          coach platform data visibility).
        </li>
      </ul>
    </section>

    <!-- 10 -->
    <section id="dpf">
      <h2>10. Data Privacy Framework (DPF) &amp; Multi-Tier Recourse Architecture</h2>
      <div class="plain">
        <span class="plain-label">In plain English</span>
        <p>
          We participate in the EU-U.S., UK Extension and Swiss-U.S. Data
          Privacy Frameworks. If you have an unresolved privacy concern, you can
          access free independent dispute resolution through BBB National
          Programs, European representation via DataRep, or binding
          arbitration.
        </p>
      </div>
      <p>
        <strong>10.1 Data Privacy Framework Certification.</strong> TalentSage
        LLC complies with the EU-U.S. Data Privacy Framework (EU-U.S. DPF), the
        UK Extension to the EU-U.S. DPF and the Swiss-U.S. Data Privacy
        Framework (Swiss-U.S. DPF) as set forth by the U.S. Department of
        Commerce regarding personal data transferred from the EU, UK, and
        Switzerland to the United States.
      </p>
      <p>
        <strong>10.2 Complaint Resolution Pathway.</strong> TalentSage provides
        a multi-tiered global recourse structure to address all data privacy
        inquiries and grievances promptly and transparently:
      </p>
      <ul>
        <li>
          <strong>Tier 1 - Internal Privacy Office.</strong> Submit your inquiry
          directly to the TalentSage Head of Privacy at Privacy@TalentSage.com.
          We investigate and respond to all inquiries within 30 days.
        </li>
        <li>
          <strong>Tier 2 - European Representative (DataRep).</strong> For
          individuals in the EU/EEA and Switzerland, you may contact our
          official EU Data Protection Representative, DataRep, via email at
          datarequest@datarep.com (or dsa@datarep.com for EU Digital Services
          Act matters) or online at
          <a href="https://www.datarep.com/datarequest" target="_blank" rel="noopener noreferrer">www.datarep.com/datarequest</a>.
          Single Point of Contact for DSA: Privacy@TalentSage.com.
        </li>
        <li>
          <strong>Tier 3 - Independent Dispute Resolution (BBB National
          Programs).</strong> If a privacy grievance cannot be resolved through
          internal channels, TalentSage has agreed to participate in independent
          dispute resolution administered free of charge by BBB National
          Programs DPF Services. To file a complaint, visit
          <a href="https://www.bbbprograms.org/dpf-complaints" target="_blank" rel="noopener noreferrer">www.bbbprograms.org/dpf-complaints</a>.
        </li>
        <li>
          <strong>Tier 4 - Binding DPF Arbitration.</strong> Under specific
          conditions detailed on the official DPF website, individuals may
          invoke binding arbitration through the DPF Panel (Annex I) for
          residual claims not resolved by other recourse mechanisms.
        </li>
        <li>
          <strong>Tier 5 - Regulatory Oversight (US FTC &amp; National
          DPAs).</strong> The U.S. Federal Trade Commission (FTC) has
          investigatory and enforcement jurisdiction over TalentSage LLC's
          compliance with the EU-U.S. DPF, UK Extension, and Swiss-U.S. DPF.
          Individuals also maintain the right to lodge a complaint with their
          national Data Protection Authority (such as the UK ICO or EU DPAs).
        </li>
      </ul>
    </section>
  </LegalPage>
</template>
